import { markBootup } from './app/shared/utils/performance.utils';
markBootup('bootup:start');

import * as Sentry from '@sentry/angular';

import {
  enableProdMode,
  ErrorHandler,
  APP_INITIALIZER,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  bootstrapApplication,
  REMOVE_STYLES_ON_COMPONENT_DESTROY,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withPreloading, PreloadAllModules } from '@angular/router';
import { RouteReuseStrategy } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { IonicRouteStrategy } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideTransloco, provideTranslocoTranspiler } from '@jsverse/transloco';
import { provideMarkdown } from 'ngx-markdown';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';
import { loadThemeFromUrl } from './app/core/services/theme/theme-loader.utils';
import { OperatorService } from './app/core/services/operator/operator.service';
import { AuthInterceptor } from './app/core/interceptors/auth.interceptor';
import { DemoInterceptor } from './app/core/interceptors/demo.interceptor';
import { TranslationService } from './app/core/services/translation/translation.service';
import { FirebaseService } from './app/core/services/firebase/firebase.service';

import posthog from 'posthog-js';

// Preload modules to prevent loading chunk failed error when offline
import '@ionic/core/dist/esm/ion-alert.entry.js';
import '@ionic/core/dist/esm/ion-loading.entry.js';
import '@ionic/core/dist/esm/ion-modal.entry.js';
import '@ionic/core/dist/esm/ion-toast.entry.js';
import '@ionic/core/dist/esm/ion-action-sheet.entry.js';
import '@ionic/core/dist/esm/ion-popover.entry.js';
import '@ionic/core/dist/esm/ion-ripple-effect.entry.js';
import { TranslocoHttpLoader } from './app/core/services/translation/transloco-loader';
import { TranslocoCustomTranspiler } from './app/core/services/translation/transloco-custom-transpiler';

// Sentry initialization
Sentry.init({
  dsn: 'https://9d97bac2087f3ed9eaca8c98b45c6a42@o4508852223737856.ingest.us.sentry.io/4509207356506112',
  release: `widget@${environment.version}`,
  environment: environment.target === 'production' ? 'production' : 'sandbox',
  dist: `${environment.build}`,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: environment.target === 'production' ? 0.05 : 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/play\.clinch\.gg\//],
  sendDefaultPii: true,
  debug: false,
  enabled: environment.target !== 'localhost',
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Filter out Phaser cross-origin errors when destroying mouse listeners
    if (error instanceof DOMException && error.name === 'SecurityError') {
      console.log(`Found security error: ${error.message}`);
      if (
        error.message.includes('removeEventListener') ||
        error.message.includes('Blocked a frame with origin')
      ) {
        return null;
      }
    }
    return event;
  },
});

if (environment.posthogApiKey && !OperatorService.IsDemo) {
  posthog.init(environment.posthogApiKey, {
    api_host: environment.posthogApiHost,
    defaults: '2026-01-30',
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
    capture_performance: { web_vitals: false },
    persistence: 'localStorage',
  });
}

if (environment.target === 'production') {
  enableProdMode();
}

function initializeTranslation(translationService: TranslationService) {
  return () => translationService.preloadLanguage();
}

// Load the operator-specific theme CSS before bootstrapping Angular
loadThemeFromUrl().then(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      // Ionic
      provideZoneChangeDetection(),
      // useSetInputAPI makes modal/popover componentProps go through setInput, so
      // signal inputs receive them; the default Object.assign overwrites an InputSignal.
      // Every componentProps key must then be a declared input: an undeclared key is
      // dropped, which only dev builds report (NG0303 console error); production is silent.
      provideIonicAngular({ rippleEffect: true, mode: 'md', useSetInputAPI: true }),
      { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },

      // Routing
      provideRouter(routes, withPreloading(PreloadAllModules)),

      // HTTP with class-based interceptors
      provideHttpClient(withInterceptorsFromDi()),
      provideMarkdown(),
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: DemoInterceptor, multi: true },

      // Animations
      provideAnimations(),

      // Transloco
      provideTransloco({
        config: {
          availableLangs: ['en', 'fi', 'pt_BR', 'fr', 'de', 'es', 'ru'],
          defaultLang: 'en',
          fallbackLang: 'en',
          missingHandler: {
            useFallbackTranslation: true,
          },
          reRenderOnLangChange: true,
          prodMode: environment.target === 'production',
        },
        loader: TranslocoHttpLoader,
      }),
      provideTranslocoTranspiler(TranslocoCustomTranspiler),

      // Ionic Storage (no standalone API — use bridge)
      importProvidersFrom(IonicStorageModule.forRoot()),

      // Sentry
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: Sentry.TraceService, deps: [Router] },

      // Firebase (must initialize before auth interceptor runs)
      {
        provide: APP_INITIALIZER,
        useFactory: (fb: FirebaseService) => () => fb,
        deps: [FirebaseService],
        multi: true,
      },

      // Translation initializer
      {
        provide: APP_INITIALIZER,
        useFactory: initializeTranslation,
        deps: [TranslationService, Sentry.TraceService],
        multi: true,
      },

      // Misc
      { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
      FirebaseService,
      TranslationService,
    ],
  }).catch((err) => console.log(err));
});
